<template>
  <div
    class="view-contact-form ws-min-height-1000 ws-typography bg-ws-white q-pb-5xl"
  >
    <div class="top-section q-pb-4xl q-px-md">
      <div class="col-12 text-center">
        <div class="title-wrapper q-mx-auto">
          <h2 :class="[mobile ? 'q-mb-sm' : 'q-mb-xl']">
            {{ $t(mainMessage.title) }}
          </h2>
          <p v-if="mainMessage.info" class="q-mb-none">
            {{ $t(mainMessage.info) }}
          </p>
          <ws-message v-if="step === 1" type="alert" path="general" />
        </div>
      </div>
    </div>

    <sidebar-layout class="rest-section q-px-md">
      <template
        v-if="[CF_USECASE.SERVER, CF_USECASE.MONITORING].includes(usecase)"
      >
        <!-- intetntional period 1 to disaply price for one month only -->
        <product-info
          v-if="productInfo"
          :product-info="productInfo"
          class="q-mb-md"
          :period="model.period"
          @period-change="model.period = $event"
        />
        <sidebar-layout-item
          v-else-if="loadingInfo || !productInfo"
          class="product-info q-mb-md"
        >
          <q-skeleton
            v-if="loadingInfo"
            type="QRange"
            animation="fade"
            height="200px"
          />
          <ws-message v-if="!productInfo" type="alert" :path="scfInfoPath" />
        </sidebar-layout-item>
      </template>

      <template v-if="loadingOptions">
        <sidebar-layout-item class="contact-form-step1 q-mb-md">
          <q-skeleton type="QRange" animation="fade" height="800px" />
        </sidebar-layout-item>
      </template>

      <template v-if="productOptions">
        <contact-form-additional
          v-if="usecase !== CF_USECASE.MANAGEMENT"
          v-model="model"
          :usecase="usecase"
          :product-options="productOptions"
          :in-edit="step === 1"
          class="q-mb-md"
          @continue="step = 2"
          @edit="step = 1"
        />

        <transition name="slide-fade-vertical">
          <contact-form-user-data
            v-if="step === 2"
            ref="userData"
            v-model="model"
            class="q-mb-md"
            :usecase="usecase"
            :product-options="productOptions"
          />
        </transition>
      </template>

      <sidebar-layout-item
        :hidden="loadingOptions || productOptions"
        class="q-mb-md"
      >
        <ws-message type="alert" :path="cfOptionsPath" />
      </sidebar-layout-item>

      <template v-slot:side>
        <properties-summary
          :info="productInfo"
          :options="productOptions"
          :model="model"
          :path="
            usecase === CF_USECASE.MONITORING
              ? 'product.properties.monitoring'
              : undefined
          "
          :properties="summaryProperties[usecase]"
          :simple-items="
            [CF_USECASE.MANAGEMENT, CF_USECASE.MONITORING].includes(usecase)
          "
          :default-price="[CF_USECASE.MANAGEMENT].includes(usecase) ? 0 : null"
        >
          <template v-if="productInfo && usecase === CF_USECASE.SERVER" v-slot:info>
            {{ productInfo.cpuName }}, {{ productInfo.ram }}GB,
            {{ productInfo.primaryDrives }}
          </template>
          <template v-if="step === 2">
            <ws-button
              class="q-mt-md"
              :type="WsButtonEnum.Type.PRIMARY"
              :disable="checkoutInProgress"
              full-width
              @click="submit"
            >
              {{ $t('summary.finish_order') }}
            </ws-button>
            <ws-message type="alert" path="general" />
          </template>

          <template v-slot:empty>
            <div class="row items-center no-wrap q-mt-md q-pt-sm">
              <div class="emoji q-mr-lg">
                😢
              </div>
              <div class="ws-typo-button text-ws-base120">
                {{ $t('product.properties.monitoring.basic.label') }}
              </div>
            </div>
          </template>
        </properties-summary>
      </template>
    </sidebar-layout>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { QSkeleton } from 'quasar';

import { cloneDeep, get } from 'lodash-es';
import WsMessage from '../components/WsMessageOld.vue';
import { WsButton, WsButtonEnum } from '@odin/components-vue2';
import { safePush } from '@/router';
import {
  getManagementInfo,
  getMonitoringInfo,
  getMonitoringOptions,
  getServerInfo,
  getServerOptions,
  orderManagement,
  orderMonitoring,
  orderServer,
} from '@/services/scf-api.service';
import SidebarLayout from '@/components/SidebarLayout.vue';
import SidebarLayoutItem from '@/components/SidebarLayoutItem.vue';
import PropertiesSummary from '@/components/PropertiesSummary.vue';
import ProductInfo from '@/components/contactForm/ProductInfo.vue';
import ContactFormAdditional from '@/components/contactForm/ContactFormAdditional.vue';
import ContactFormUserData from '@/components/contactForm/ContactFormUserData.vue';
import { CF_USECASE } from '@/components/contactForm/const.enum';
import {Throttle} from '@odin/utils';

const defaultModel = {
  name: '',
  contact: {
    login: '',
    name: '',
    email: '',
  },
};

const defaultServerModel = {
  ...defaultModel,
  ipAddress: { type: 'one', price: { actualPrice: 0 } },
  monitoring: { type: 'basic', price: { actualPrice: 0 } },
  os: null,
  licences: [],
  backup: { volume: 0 },
  period: 1,
};

@Component({
  components: {
    QSkeleton,
    WsMessage,
    WsButton,
    SidebarLayout,
    SidebarLayoutItem,
    PropertiesSummary,
    ProductInfo,
    ContactFormAdditional,
    ContactFormUserData,
  },
})
export default class ContactForm extends Vue {
  @Prop()
  readonly usecase!: CF_USECASE;

  loadingInfo = true;
  loadingOptions = true;

  productInfo: any = null;
  productOptions: any = null;

  scfInfoPath: string = 'scf.info';
  cfOptionsPath: string = 'scf.options';

  model: any = { ...defaultModel };
  CF_USECASE = CF_USECASE;
  WsButtonEnum = WsButtonEnum;

  step = 1;
  checkoutInProgress = false;

  get summaryProperties(): Record<string, string[]> {
    const monitoring: string[] = [];
    const productOptions = this.productOptions;
    if (!productOptions) {
      return {};
    }

    if (this.usecase === CF_USECASE.MONITORING) {
      productOptions.forEach((section: any) => {
        if (!section.options) {
          return;
        }
        section.options.forEach((option: any) => {
          if (this.model[option.name] === true) {
            monitoring.push(section.name + '.' + option.name);
          }
        });
      });
    }

    return {
      server: ['monitoring', 'ipAddress', 'os', ...this.optionalBackup],
      monitoring,
      management: [
        'systemMonitoring',
        'supervision',
        'installation',
        'tunning',
        'updates',
      ],
    };
  }

  get optionalBackup() {
    return this.model?.backup?.volume ? ['backup'] : [];
  }

  get mainMessage() {
    return {
      title: `scf.message.${this.usecase}.title`,
      info: `scf.message.${this.usecase}.info`,
    };
  }

  async created() {
    if (this.usecase === CF_USECASE.MANAGEMENT) {
      this.step = 2;
    }

    this.resetModel();
    this.getProductInfo();
    this.getOptions();
  }

  resetModel() {
    let model: Record<string, any> = { ...defaultModel };
    if ([CF_USECASE.MANAGEMENT, CF_USECASE.MONITORING].includes(this.usecase)) {
      model.period = 1;
    }
    if (this.usecase === CF_USECASE.SERVER) {
      model = { ...defaultServerModel };
    } else {
      this.summaryProperties[this.usecase]?.forEach((prop: string) => {
        model[prop] = false;
      });
    }
    this.model = model;
  }

  async getProductInfo() {
    this.loadingInfo = true;
    const { uuid, period } = get(this.$route, 'query', {});
    if (period) {
      this.model.period = parseInt(period);
    }

    try {
      if (this.usecase === CF_USECASE.SERVER) {
        this.model.dedicatedServerUuid = uuid;

        if (this.model.dedicatedServerUuid) {
          const response = await getServerInfo(this.model.dedicatedServerUuid);
          this.productInfo = {
            ...response.data,
            usecase: CF_USECASE.SERVER,
          };
        } else {
          this.$nextTick(() => {
            // wait for ws-message component to be mounted
            this.$messageService.errorHandler(this.scfInfoPath)(
              'scf.error.uuid_required'
            );
          });
        }
      } else if (this.usecase === CF_USECASE.MONITORING) {
        const response: any = await getMonitoringInfo();
        this.productInfo = {
          usecase: CF_USECASE.MONITORING,
          prices: response.data?.prices,
          title: 'scf.monitoring_info.title',
        };
      } else if (this.usecase === CF_USECASE.MANAGEMENT) {
        const response: any = await getManagementInfo();
        this.productInfo = {
          usecase: CF_USECASE.MANAGEMENT,
          prices: response.data?.prices,
          title: 'scf.management_info.title',
        };
      }
    } catch (err) {
      this.$messageService.errorHandler(this.scfInfoPath)(err);
    }
    this.loadingInfo = false;
  }

  async getOptions() {
    try {
      if (this.usecase === CF_USECASE.MONITORING) {
        const response = await getMonitoringOptions();

        // TODO require BE to send it already in this format: {name: string, options: string[]}[]
        const productOptions = Object.entries(
          response.data
        ).map(([key, value]) => ({ name: key, options: value }));
        // initiate model
        productOptions.forEach((section: any) => {
          section.options.forEach((option: any) => {
            Vue.set(this.model, option.name, false);
          });
        });
        this.productOptions = productOptions;
      } else if (this.usecase === CF_USECASE.SERVER) {
        const response = await getServerOptions();
        const productOptions = response.data;
        if (productOptions.backup) {
          this.model.backup = {
            ...this.model.backup,
            ...productOptions.backup,
          };
        }
        this.productOptions = productOptions;
      } else {
        this.productOptions = {};
      }
    } catch (err) {
      this.$messageService.errorHandler(this.cfOptionsPath)(err);
    }

    this.loadingOptions = false;
  }

  @Throttle(1000, {trailing: false})
  async submit() {
    this.$messageService.clearMessages('general');

    const userDataForm = this.$refs.userData as ContactFormUserData;
    const validData = await userDataForm.validate();

    if (!validData) {
      return;
    }

    const requestData = this.getRequestData(
      userDataForm.userExistence === 'old'
    );
    this.checkoutInProgress = true;

    try {
      await ({
        server: orderServer,
        monitoring: orderMonitoring,
        management: orderManagement,
        // eslint-disable-next-line no-unused-vars
      } as Record<CF_USECASE, (requestData: any) => void>)[this.usecase](
        requestData
      );
      // clear model
      this.resetModel();
      safePush({ name: 'thank-you', query: { messageKey: 'mail' } });
    } catch (err) {
      this.$messageService.errorHandler('scf')(err);
    }

    this.checkoutInProgress = false;
  }

  getRequestData(oldUser: boolean) {
    let requestData = cloneDeep(this.model);

    if (oldUser) {
      delete requestData.contact.name;
      delete requestData.contact.organisation;
      delete requestData.contact.email;
      delete requestData.contact.phone;
    } else {
      delete requestData.contact.login;
    }

    if (this.usecase === CF_USECASE.SERVER) {
      const licences = requestData.licences.length
        ? requestData.licences.map((l: any) => l.type)
        : undefined;
      requestData = {
        ...requestData,
        // cleaning and transforming for BE model requirements
        monitoring: requestData.monitoring.type,
        ipAddress: requestData.ipAddress.type,
        backup: requestData.backup.volume || undefined,
        os: requestData.os.type || undefined,
        licences,
      };
    }

    if (this.usecase === CF_USECASE.MONITORING) {
      // workaround until fixed on BE
      // ref: https://loopiagroup.atlassian.net/browse/PNO-2749
      requestData.email = requestData.contact.email;
    }

    return requestData;
  }
}
</script>

<style lang="scss" scoped>
.view-contact-form {
  > .top-section {
    background: $ws-gradient-light;
    padding-top: 104px;

    .title-wrapper {
      max-width: 850px;
    }
  }

  > .rest-section {
    max-width: calc(1251px + 32px /* padding */);
    margin: auto;

    .product-info {
      > .price-wrapper {
        > .price {
          font-size: $ws-font-size-md2;
        }
      }

      > .price-desktop {
        position: absolute;
        top: map-get($space-xl, y);
        right: map-get($space-xl, x);
      }

      > p {
        color: $ws-base110;
      }

      > .wrapper-free {
        margin-top: map-get($space-xl, y);
        margin-left: -(map-get($space-sm, x));
        margin-right: -(map-get($space-sm, x));
        width: 100%;
        color: $ws-base150;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-content: flex-start;
        align-items: center;

        > .free-item {
          flex: 0 1 auto;

          > .wrapper {
            display: inline-block;
            white-space: nowrap;
            margin: map-get($space-xs, y) map-get($space-sm, x);
          }
        }
      }
    }
  }

  .in-edit {
    .theme--cary & {
      @include ws-top-stripe;
    }
  }

  .cmp-sidebar-layout {
    &::v-deep.cmp-sidebar-layout-item {
      margin-top: map-get($space-md, y);
      margin-right: 0;
      margin-left: 0;

      &:first-of-type {
        margin-top: 0;
      }

      &.product-info {
        border: $ws-border-thick;
        border-radius: $ws-border-radius;
      }
    }

    @media (max-width: $ws-breakpoint-tablet-max) {
      column-gap: map-get($space-lg, x);
    }

    @media (max-width: $ws-breakpoint-mobile-max) {
      padding: 0;
    }
  }

  .emoji {
    font-size: 2.5rem;
  }
}
</style>
